// 🛠️ Developer Hub - Unified Developer Dashboard

export function DeveloperHub() {
  const tools = [
    {
      id: 'system-test',
      icon: '🧪',
      title: 'System Test',
      description: 'Verify all integrations are working correctly',
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700',
      route: '#test',
      features: [
        'Test environment variables',
        'Verify API keys',
        'Check database connection',
        'Test third-party SDKs',
      ],
      stats: {
        label: 'Tests',
        value: '6',
      },
    },
    {
      id: 'file-map',
      icon: '📁',
      title: 'File Map',
      description: 'Visual project structure with AI descriptions',
      color: 'from-teal-500 to-teal-600',
      hoverColor: 'hover:from-teal-600 hover:to-teal-700',
      route: '#files',
      features: [
        'See all project files',
        'AI-generated descriptions',
        'Importance indicators',
        'Category filtering',
      ],
      stats: {
        label: 'Files',
        value: '40+',
      },
    },
    {
      id: 'links-inventory',
      icon: '🔗',
      title: 'Links Inventory',
      description: 'Complete catalog of all URLs in the project',
      color: 'from-purple-500 to-purple-600',
      hoverColor: 'hover:from-purple-600 hover:to-purple-700',
      route: '#links',
      features: [
        'All API endpoints',
        'Frontend routes',
        'External services',
        'One-click copy URLs',
      ],
      stats: {
        label: 'Links',
        value: '25+',
      },
    },
    {
      id: 'security-audit',
      icon: '🔒',
      title: 'Security Audit',
      description: 'Vulnerability scanner and security checks',
      color: 'from-red-500 to-red-600',
      hoverColor: 'hover:from-red-600 hover:to-red-700',
      route: '#security',
      features: [
        'Check for exposed secrets',
        'Verify HTTPS usage',
        'Scan dependencies',
        'Security headers',
      ],
      stats: {
        label: 'Checks',
        value: '10',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-6xl">🛠️</div>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">Developer Hub</h1>
                <p className="text-gray-300 text-lg">
                  Self-documenting, self-auditing project dashboard
                </p>
              </div>
            </div>
            <a
              href="/"
              className="text-sm text-teal-400 hover:text-teal-300 font-medium transition"
            >
              ← Back to Dashboard
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Everything you need to understand your project
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Automated testing, documentation, security audits, and code navigation - all in one place
          </p>
        </div>

        {/* Tool Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {tools.map(tool => (
            <a
              key={tool.id}
              href={tool.route}
              className={`block bg-gradient-to-r ${tool.color} ${tool.hoverColor} rounded-xl shadow-2xl p-8 text-white transition-all transform hover:scale-105 hover:shadow-3xl`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="text-6xl">{tool.icon}</div>
                <div className="text-right">
                  <div className="text-sm opacity-90">{tool.stats.label}</div>
                  <div className="text-3xl font-bold">{tool.stats.value}</div>
                </div>
              </div>

              <h3 className="text-2xl font-bold mb-3">{tool.title}</h3>
              <p className="text-white/90 mb-6">{tool.description}</p>

              <div className="space-y-2">
                {tool.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-lg">✓</span>
                    <span className="text-white/90">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <span className="text-sm font-semibold">Click to explore →</span>
              </div>
            </a>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-3 gap-6 mb-12">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
            <div className="text-4xl mb-3">⚡</div>
            <h3 className="text-lg font-bold text-white mb-2">Instant Verification</h3>
            <p className="text-sm text-gray-400">
              Run all tests in seconds and know exactly what's broken
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
            <div className="text-4xl mb-3">📝</div>
            <h3 className="text-lg font-bold text-white mb-2">Auto-Documentation</h3>
            <p className="text-sm text-gray-400">
              AI-generated descriptions for every file and URL
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
            <div className="text-4xl mb-3">🛡️</div>
            <h3 className="text-lg font-bold text-white mb-2">Security First</h3>
            <p className="text-sm text-gray-400">
              Continuous security scanning and vulnerability detection
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 mb-12">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            📊 Project Health Overview
          </h3>
          <div className="grid grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">6/6</div>
              <div className="text-sm text-gray-400">System Tests</div>
              <div className="text-xs text-green-400 mt-1">All Passing</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">40+</div>
              <div className="text-sm text-gray-400">Project Files</div>
              <div className="text-xs text-teal-400 mt-1">Documented</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">25+</div>
              <div className="text-sm text-gray-400">URLs Cataloged</div>
              <div className="text-xs text-purple-400 mt-1">APIs & Routes</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-400 mb-2">0</div>
              <div className="text-sm text-gray-400">Security Issues</div>
              <div className="text-xs text-green-400 mt-1">Secure</div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-lg border border-blue-700/50 p-8">
          <h3 className="text-2xl font-bold text-white mb-6">
            ✨ Why Use the Developer Hub?
          </h3>
          <div className="grid grid-cols-2 gap-6 text-white">
            <div>
              <h4 className="font-semibold mb-2 text-blue-300">For New Developers:</h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• Understand the entire codebase in 10 minutes</li>
                <li>• See what every file does and why it exists</li>
                <li>• Find any URL or endpoint instantly</li>
                <li>• Verify your local setup is correct</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2 text-purple-300">For DevOps/Security:</h4>
              <ul className="space-y-1 text-sm text-gray-300">
                <li>• Audit all external dependencies</li>
                <li>• Check for security vulnerabilities</li>
                <li>• Verify HTTPS and authentication</li>
                <li>• Monitor secrets and configuration</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 mt-12 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            🚀 Start Exploring
          </h3>
          <p className="text-gray-400 mb-6">
            Pick a tool above to get started, or run all checks at once
          </p>
          <div className="flex gap-4 justify-center">
            <a
              href="#test"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition"
            >
              Run System Test
            </a>
            <a
              href="#security"
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition"
            >
              Security Audit
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-gray-500 text-sm">
          <p>
            Built with ❤️ using the{' '}
            <span className="text-teal-400 font-semibold">System Test Template</span>
          </p>
          <p className="mt-2">
            Every project should have a Developer Hub. Make yours today!
          </p>
        </div>
      </div>
    </div>
  );
}
